import { getRedditSentiment } from './redditService';
import { getYouTubeSentiment } from './youtubeService';
import { getAffiliatePurchaseOptions } from './affiliateService';

// Combine Reddit and YouTube sentiment data
const combineSentimentData = (reddit, youtube) => {
  const redditData = reddit || { positive: 0, neutral: 0, negative: 0, total_posts_analyzed: 0, top_mentions: [], recent_trend: 'stable', overall_percentage: 50 };
  const youtubeData = youtube || { positive: 0, neutral: 0, negative: 0, total_comments_analyzed: 0, top_mentions: [], recent_trend: 'stable', overall_percentage: 50 };

  const totalPosts = redditData.total_posts_analyzed || 0;
  const totalComments = youtubeData.total_comments_analyzed || 0;
  const combinedTotal = totalPosts + totalComments;

  if (combinedTotal === 0) {
    return {
      overall: 0,
      overall_percentage: 50,
      positive: 0,
      neutral: 0,
      negative: 0,
      recent_trend: 'stable',
      top_mentions: [],
      combined_total: 0,
      reddit_posts: 0,
      youtube_comments: 0
    };
  }

  const positive = (redditData.positive * totalPosts + youtubeData.positive * totalComments) / combinedTotal;
  const neutral = (redditData.neutral * totalPosts + youtubeData.neutral * totalComments) / combinedTotal;
  const negative = (redditData.negative * totalPosts + youtubeData.negative * totalComments) / combinedTotal;

  const overall = (positive - negative) / 100;
  const overall_percentage = Math.round(((overall + 1) / 2) * 100);

  const allMentions = [
    ...(redditData.top_mentions || []).map(m => ({
      ...m,
      platform: 'Reddit',
      likes: m.score,
      text: m.text
    })),
    ...(youtubeData.top_mentions || []).map(m => ({
      ...m,
      platform: 'YouTube',
      text: m.text
    }))
  ];

  allMentions.sort((a, b) => (b.likes || b.score || 0) - (a.likes || a.score || 0));
  const topMentions = allMentions.slice(0, 3);

  const trendWeights = { rising: 1, falling: -1, stable: 0 };
  const redditTrend = trendWeights[redditData.recent_trend] || 0;
  const youtubeTrend = trendWeights[youtubeData.recent_trend] || 0;
  const combinedTrendScore = (redditTrend * totalPosts + youtubeTrend * totalComments) / combinedTotal;

  let recent_trend = 'stable';
  if (combinedTrendScore > 0.2) recent_trend = 'rising';
  else if (combinedTrendScore < -0.2) recent_trend = 'falling';

  return {
    overall,
    overall_percentage,
    positive: Math.round(positive),
    neutral: Math.round(neutral),
    negative: Math.round(negative),
    recent_trend,
    top_mentions: topMentions,
    combined_total: combinedTotal,
    reddit_posts: totalPosts,
    youtube_comments: totalComments
  };
};

export const callOpenAI = async ({ brand, model, question, category }) => {
  try {
    console.log(`Fetching Reddit sentiment for ${brand} ${model}...`);
    const redditSentiment = await getRedditSentiment(brand, model);
    console.log('Reddit sentiment:', redditSentiment);

    console.log(`Fetching YouTube sentiment for ${brand} ${model}...`);
    const youtubeSentiment = await getYouTubeSentiment(brand, model);
    console.log('YouTube sentiment:', youtubeSentiment);

    const combinedSentiment = combineSentimentData(redditSentiment, youtubeSentiment);
    console.log('Combined sentiment:', combinedSentiment);

    const affiliateOptions = getAffiliatePurchaseOptions(brand, model);

    const redditTopMentions = redditSentiment?.top_mentions?.map(m => `"${m.text}" (${m.sentiment})`).join(', ') || '';
    const redditContext = redditSentiment && redditSentiment.total_posts_analyzed > 0
      ? `Reddit: ${redditSentiment.total_posts_analyzed} posts. ${redditSentiment.positive}% positive, ${redditSentiment.neutral}% neutral, ${redditSentiment.negative}% negative. Trend: ${redditSentiment.recent_trend}. Top discussions: ${redditTopMentions}`
      : 'No Reddit data.';

    const youtubeTopMentions = youtubeSentiment?.top_mentions?.map(m => `"${m.text}" (${m.sentiment})`).join(', ') || '';
    const youtubeContext = youtubeSentiment && youtubeSentiment.total_comments_analyzed > 0
      ? `YouTube: ${youtubeSentiment.total_comments_analyzed} comments. ${youtubeSentiment.positive}% positive, ${youtubeSentiment.neutral}% neutral, ${youtubeSentiment.negative}% negative. Top comments: ${youtubeTopMentions}`
      : 'No YouTube data.';

    console.log('Calling OpenRouter API for:', { brand, model, question, category });

    // Call our secure server-side proxy instead of OpenRouter directly
    const response = await fetch('/api/ai', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        model: 'openai/gpt-4o-mini',
        messages: [
          {
            role: 'system',
            content: `You are a product expert. Analyze ${brand} ${model}${category ? ` (${category})` : ''}.

Community data:
${redditContext}
${youtubeContext}

The user has asked: "${question}"

Your job is to answer their question thoroughly using 2-3 sections with bullet points. The section titles must be directly relevant to what the user asked — do NOT use generic titles like "Overview" or "Summary".

Examples:
- "Is the battery good?" → sections: "Battery Life", "Charging Speed", "Community Verdict"
- "What are the pros and cons?" → sections: "Key Strengths", "Weaknesses", "Overall Verdict"
- "Is it good for gaming?" → sections: "Gaming Performance", "Thermal Management", "Community Take"
- "How's the camera?" → sections: "Photo Quality", "Video Capabilities", "Low Light Performance"

Return ONLY a valid JSON object — no markdown, no code blocks, no extra text:
{
  "verdict": "Recommended",
  "answer_to_question": "One concise sentence directly answering the question",
  "detailed_summary": {
    "sections": [
      {
        "title": "Section title relevant to the question",
        "points": ["Specific insight 1", "Specific insight 2", "Specific insight 3"]
      },
      {
        "title": "Section title relevant to the question",
        "points": ["Specific insight 1", "Specific insight 2"]
      },
      {
        "title": "Community Verdict",
        "points": ["What Reddit/YouTube users say about this specific topic"]
      }
    ]
  },
  "rating_info": {
    "average_rating": 4.5,
    "total_reviews": 1000
  },
  "pros": ["pro1", "pro2", "pro3"],
  "cons": ["con1", "con2"],
  "alternatives": [
    {
      "brand": "Brand",
      "model": "Model",
      "reason": "Why this is a good alternative",
      "price_comparison": "Price comparison"
    }
  ]
}

Rules:
- verdict: must be exactly one of "Recommended", "Mixed", or "Not Recommended"
- answer_to_question: one sentence, direct answer to the question
- detailed_summary.sections: exactly 3 sections, each with 2-3 bullet points
- Section titles must reflect the user's question, not generic product categories
- pros: exactly 3 items, derived from real user feedback in the community data above
- cons: exactly 2 items, derived from real user feedback in the community data above
- If community data is limited, use your product knowledge but frame it as "users report" or "reviewers note"
- alternatives: exactly 1 item
- rating_info: only average_rating and total_reviews
- Do NOT include purchase_options`
          },
          {
            role: 'user',
            content: `Question: ${question}`
          }
        ],
        temperature: 0.7,
        max_tokens: 1500,
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('OpenRouter API error:', response.status, errorText);

      if (response.status === 429 || response.status === 402) {
        console.warn('API rate limit reached, using mock data with real sentiment');
        return getMockResponse(brand, model, question, category, redditSentiment, youtubeSentiment, combinedSentiment, affiliateOptions);
      }

      throw new Error(`OpenRouter API error: ${response.status}`);
    }

    const data = await response.json();
    console.log('OpenRouter API response received');

    try {
      const content = data.choices[0].message.content;
      console.log('Raw AI response received, length:', content.length);

      let cleanContent = content.trim();

      if (cleanContent.includes('```json')) {
        cleanContent = cleanContent.split('```json')[1].split('```')[0].trim();
      } else if (cleanContent.includes('```')) {
        cleanContent = cleanContent.split('```')[1].split('```')[0].trim();
      }

      if (!cleanContent.startsWith('{')) {
        const jsonMatch = cleanContent.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          cleanContent = jsonMatch[0];
        }
      }

      console.log('Cleaned content length:', cleanContent.length);

      if (!cleanContent.endsWith('}')) {
        console.warn('Response appears truncated, falling back to mock');
        return getMockResponse(brand, model, question, category, redditSentiment, youtubeSentiment, combinedSentiment, affiliateOptions);
      }

      const parsedResponse = JSON.parse(cleanContent);
      console.log('Successfully parsed JSON response');

      const finalResponse = {
        ...parsedResponse,
        purchase_options: affiliateOptions,
        reddit_sentiment: redditSentiment || null,
        youtube_sentiment: youtubeSentiment || null,
        social_sentiment: combinedSentiment
      };

      return finalResponse;
    } catch (parseError) {
      console.error('Failed to parse JSON response:', parseError);
      console.error('Raw content that failed (first 500 chars):', data.choices[0].message.content.substring(0, 500));
      return getMockResponse(brand, model, question, category, redditSentiment, youtubeSentiment, combinedSentiment, affiliateOptions);
    }
  } catch (error) {
    console.error('OpenRouter call failed:', error);
    const redditSentiment = await getRedditSentiment(brand, model);
    const youtubeSentiment = await getYouTubeSentiment(brand, model);
    const combinedSentiment = combineSentimentData(redditSentiment, youtubeSentiment);
    const affiliateOptions = getAffiliatePurchaseOptions(brand, model);
    return getMockResponse(brand, model, question, category, redditSentiment, youtubeSentiment, combinedSentiment, affiliateOptions);
  }
};

const getMockResponse = (brand, model, question, category, redditSentiment, youtubeSentiment, combinedSentiment, affiliateOptions) => {
  const questionLower = question?.toLowerCase() || '';
  let keyPhrase = 'overall satisfaction';
  let sections = [];

  if (questionLower.includes('battery')) {
    keyPhrase = 'battery life';
    sections = [
      { title: 'Battery Life', points: ['All-day battery life under moderate usage', 'Heavy usage may require a top-up by evening', 'Background apps managed efficiently'] },
      { title: 'Charging Speed', points: ['Fast charging support reduces downtime', 'Wireless charging available on most configurations'] },
      { title: 'Community Verdict', points: ['Reddit users generally praise battery improvements over previous gen', 'YouTube reviewers rate endurance above average for the category'] }
    ];
  } else if (questionLower.includes('camera')) {
    keyPhrase = 'camera quality';
    sections = [
      { title: 'Photo Quality', points: ['Excellent detail in good lighting conditions', 'AI scene detection enhances shots automatically', 'Portrait mode produces natural bokeh'] },
      { title: 'Video Capabilities', points: ['Smooth 4K recording with good stabilization', 'Night video improved over previous generation'] },
      { title: 'Community Verdict', points: ['Camera is frequently praised in community discussions', 'Users highlight low-light performance as a standout'] }
    ];
  } else if (questionLower.includes('gaming')) {
    keyPhrase = 'gaming performance';
    sections = [
      { title: 'Gaming Performance', points: ['Handles most titles at high settings smoothly', 'Consistent frame rates in popular games', 'Low latency touch response'] },
      { title: 'Thermal Management', points: ['Stays cool during short sessions', 'Some throttling reported in extended gaming'] },
      { title: 'Community Verdict', points: ['Gamers rate it highly for casual to mid-core gaming', 'Competitive gamers praise the display refresh rate'] }
    ];
  } else if (questionLower.includes('price') || questionLower.includes('worth')) {
    keyPhrase = 'value for money';
    sections = [
      { title: 'Value Proposition', points: ['Competitive pricing for the feature set offered', 'Holds its value well compared to rivals', 'Frequent promotions available on Shopee and Amazon'] },
      { title: 'What You Get', points: ['Premium build quality at a mid-range price point', 'Software support extends useful lifespan'] },
      { title: 'Community Verdict', points: ['Most users feel it offers good value', 'Commonly recommended as a best-buy in its segment'] }
    ];
  } else {
    sections = [
      { title: 'Key Strengths', points: [`Excellent ${keyPhrase} performance`, 'Reliable build quality and durable construction', 'Competitive pricing for the features offered'] },
      { title: 'Weaknesses', points: ['Battery life could be improved for heavy usage', 'Premium price point for higher-end models'] },
      { title: 'Community Verdict', points: [`Community sentiment is ${combinedSentiment?.positive || 0}% positive`, 'Generally well-received across Reddit and YouTube'] }
    ];
  }

  const redditData = redditSentiment || { overall_percentage: 50, positive: 0, neutral: 0, negative: 0, recent_trend: 'stable', total_posts_analyzed: 0, top_mentions: [] };
  const youtubeData = youtubeSentiment || { overall_percentage: 50, positive: 0, neutral: 0, negative: 0, total_comments_analyzed: 0, top_mentions: [] };
  const totalReviews = redditData.total_posts_analyzed + youtubeData.total_comments_analyzed;
  const combined = combinedSentiment || { positive: 0, neutral: 0, negative: 0, combined_total: 0 };

  const verdictScore = combined.positive || 0;
  const verdict = verdictScore >= 60 ? 'Recommended' : verdictScore >= 40 ? 'Mixed' : 'Not Recommended';

  return {
    verdict,
    answer_to_question: `The ${brand} ${model} performs well for ${keyPhrase} and is generally a solid choice in its category.`,
    detailed_summary: { sections },
    rating_info: {
      average_rating: combined.positive > 0 ? (combined.positive / 20) : 4.2,
      total_reviews: totalReviews || 847
    },
    pros: [
      `Excellent ${keyPhrase} performance with consistent results`,
      'Reliable build quality and durable construction',
      'Competitive pricing for the features offered'
    ],
    cons: [
      'Battery life could be improved for heavy usage',
      'Premium price point for higher-end models'
    ],
    purchase_options: affiliateOptions,
    alternatives: [
      {
        brand: brand === 'Apple' ? 'Samsung' : 'Apple',
        model: brand === 'Apple' ? 'Galaxy S26 Ultra' : 'iPhone 17 Pro',
        reason: `Strong competitor with comparable ${keyPhrase} performance and similar feature set`,
        price_comparison: 'Similar price range'
      }
    ],
    reddit_sentiment: redditData,
    youtube_sentiment: youtubeData,
    social_sentiment: combinedSentiment || {
      overall: 0, overall_percentage: 50, positive: 0, neutral: 0, negative: 0,
      recent_trend: 'stable', top_mentions: [], combined_total: 0, reddit_posts: 0, youtube_comments: 0
    }
  };
};